import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { StartStreamingScreen } from '../components/Interior/StartStreamingScreen';
import { LandingScreen } from '../components/Interior/LandingScreen';
import { DesignQuestionScreen } from '../components/Interior/DesignQuestionScreen';
import { EmailScreen } from '../components/Interior/EmailScreen';
import { UserIdScreen } from '../components/Interior/UserIdScreen';
import { LoadingScreen } from '../components/Interior/LoadingScreen';
import { PressAnyKeyScreen } from '../components/Interior/PressAnyKeyScreen';
import { PreferenceScreen } from '../components/Interior/PreferenceScreen';
import { InteriorViewer } from '../components/Interior/InteriorViewer';
import { ThemeProvider } from '../components/styles/ThemeContext';
type InteriorScreen = 'streaming' | 'landing' | 'question' | 'email' | 'userId' | 'loading' | 'pressKey' | 'preference' | 'viewer';
function InteriorAppContent() {
  const [currentScreen, setCurrentScreen] = useState<InteriorScreen>('streaming');
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [fromEmail, setFromEmail] = useState(false);
  // Check for skip parameter on mount to bypass onboarding for power users
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldSkip = urlParams.get('skip') === 'true';
    if (shouldSkip) {
      setCurrentScreen('viewer');
      // Clean up the URL to remove the skip parameter
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }
  }, []);
  const handleStartStreaming = () => {
    setCurrentScreen('landing');
    window.history.pushState({}, '', '/interior');
  };
  const handleBeginTour = () => {
    setCurrentScreen('question');
    window.history.pushState({}, '', '/interior/question');
  };
  const handleDesignYes = () => {
    setFromEmail(false);
    setCurrentScreen('userId');
    window.history.pushState({}, '', '/interior/login');
  };
  const handleDesignNo = () => {
    setFromEmail(true);
    setCurrentScreen('email');
    window.history.pushState({}, '', '/interior/email');
  };
  const handleEmailSubmit = (email: string) => {
    setUserEmail(email);
    setCurrentScreen('userId');
    window.history.pushState({}, '', '/interior/login');
  };
  const handleUserIdSubmit = (id: string) => {
    setUserId(id);
    setCurrentScreen('loading');
    window.history.pushState({}, '', '/interior/loading');
  };
  const handleLoadingComplete = () => {
    setCurrentScreen('pressKey');
    window.history.pushState({}, '', '/interior/ready');
  };
  const handlePressKey = () => {
    setCurrentScreen('preference');
    window.history.pushState({}, '', '/interior/preference');
  };
  const handleSelectGuidedTour = () => {
    setCurrentScreen('viewer');
    window.history.pushState({}, '', '/interior/view/guided');
  };
  const handleSelectMouseKeyboard = () => {
    setCurrentScreen('viewer');
    window.history.pushState({}, '', '/interior/view/manual');
  };
  const handleBack = () => {
    if (currentScreen === 'landing') {
      setCurrentScreen('streaming');
    } else if (currentScreen === 'question') {
      setCurrentScreen('landing');
    } else if (currentScreen === 'email') {
      setCurrentScreen('question');
    } else if (currentScreen === 'userId') {
      if (fromEmail) {
        setCurrentScreen('email');
      } else {
        setCurrentScreen('question');
      }
    }
  };
  const handleExit = () => {
    window.history.pushState({}, '', '/');
    window.location.href = '/';
  };
  return <div className="relative w-full h-screen overflow-hidden" data-id="element-1140">
      <AnimatePresence mode="wait" data-id="element-1141">
        {currentScreen === 'streaming' && <motion.div key="streaming" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-1142">
            <StartStreamingScreen onStart={handleStartStreaming} data-id="element-1143" />
          </motion.div>}

        {currentScreen === 'landing' && <motion.div key="landing" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-1144">
            <LandingScreen onBeginTour={handleBeginTour} onExit={handleExit} data-id="element-1145" />
          </motion.div>}

        {currentScreen === 'question' && <motion.div key="question" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-1146">
            <DesignQuestionScreen onYes={handleDesignYes} onNo={handleDesignNo} onBack={handleBack} data-id="element-1147" />
          </motion.div>}

        {currentScreen === 'email' && <motion.div key="email" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-1148">
            <EmailScreen onSubmit={handleEmailSubmit} onBack={handleBack} data-id="element-1149" />
          </motion.div>}

        {currentScreen === 'userId' && <motion.div key="userId" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-1150">
            <UserIdScreen onSubmit={handleUserIdSubmit} onBack={handleBack} fromEmail={fromEmail} data-id="element-1151" />
          </motion.div>}

        {currentScreen === 'loading' && <motion.div key="loading" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-1152">
            <LoadingScreen onComplete={handleLoadingComplete} data-id="element-1153" />
          </motion.div>}

        {currentScreen === 'pressKey' && <motion.div key="pressKey" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-1154">
            <PressAnyKeyScreen onContinue={handlePressKey} data-id="element-1155" />
          </motion.div>}

        {currentScreen === 'preference' && <motion.div key="preference" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-1156">
            <PreferenceScreen onSelectGuidedTour={handleSelectGuidedTour} onSelectMouseKeyboard={handleSelectMouseKeyboard} data-id="element-1157" />
          </motion.div>}

        {currentScreen === 'viewer' && <motion.div key="viewer" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="w-full h-full" data-id="element-1158">
            <InteriorViewer data-id="element-1159" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function InteriorApp() {
  return <ThemeProvider data-id="element-1160">
      <InteriorAppContent data-id="element-1161" />
    </ThemeProvider>;
}